import S3 from '@uppy/aws-s3';
import Uppy, { UppyFile } from '@uppy/core';
import Bugsnag from 'common/utils/Bugsnag';
import { csrfToken } from 'common/utils/xhr';
import uuid from 'uuid/v4';

const Megabyte = 1024 * 1024;
const Minute = 60 * 1000;

export type UppyFileWithKey = UppyFile<{ key: string }>;

export interface UploaderConfig {
    requestAccessKey: string;
    requestEndpoint: string;
    signatureEndpoint: string;
    bucket: string;
    keyPrefix?: string;
    successEndpoint?: string;
    region?: string;
    signatureVersion?: number;
}

export function createUploader({
    requestAccessKey,
    requestEndpoint,
    signatureEndpoint,
    bucket,
    keyPrefix,
    region = 'us-east-1',
    signatureVersion = 2,
}: UploaderConfig): Uppy.Uppy {
    const uppy = Uppy({
        autoProceed: true,
        restrictions: {
            maxFileSize: 100 * Megabyte,
            maxNumberOfFiles: null,
            minNumberOfFiles: null,
            allowedFileTypes: [
                '.ai',
                '.bmp',
                '.cdr',
                '.cgm',
                '.clk',
                '.cur',
                '.doc',
                '.docx',
                '.emf',
                '.epi',
                '.eps',
                '.eps2',
                '.eps3',
                '.epsf',
                '.epsi',
                '.ept',
                '.exif',
                '.fla',
                '.gif',
                '.gfa',
                '.giff',
                '.ico',
                '.ind',
                '.indd',
                '.jpe',
                '.jpeg',
                '.jpg',
                '.odg',
                '.pct',
                '.pcx',
                '.pdf',
                '.pic',
                '.pict',
                '.png',
                '.pot',
                '.potx',
                '.pps',
                '.ppt',
                '.pptx',
                '.psd',
                '.ps',
                '.pub',
                '.raw',
                '.rtf',
                '.svg',
                '.svgz',
                '.swf',
                '.tif',
                '.tiff',
                '.txt',
                '.wmf',
                '.zip',
            ],
        },
        locale: {
            strings: {
                exceedsSize: 'exceedsSize',
            },
        },
    });

    uppy.on('error', (error: Error) => {
        Bugsnag.notify(error);
    })
        .on('upload-error', (file: UppyFileWithKey, error: Error) => {
            Bugsnag.notify(error, { metaData: { file } });
        })
        .on('file-added', (file: UppyFileWithKey) => {
            uppy.setFileMeta(file.id, {
                key: `${keyPrefix || ''}${uuid()}.${file.extension}`,
            });
        })
        .use(S3, {
            getUploadParameters: async (file: UppyFileWithKey) => {
                if (signatureVersion == 4) {
                    const [year, month, day, hour, min, sec] = new Date()
                        .toISOString()
                        .split(/:|\-|T|Z|\./);

                    const credentials = `${requestAccessKey}/${year}${month}${day}/${region}/s3/aws4_request`;

                    const date = `${year}${month}${day}T${hour}${min}${sec}Z`;

                    const response = await fetch(signatureEndpoint, {
                        method: 'POST',
                        headers: {
                            accept: 'application/json',
                            'X-CSRF-Token': csrfToken(),
                        },
                        body: JSON.stringify({
                            expiration: new Date(Date.now() + 5 * Minute).toISOString(),
                            conditions: [
                                { acl: 'private' },
                                { bucket: bucket },
                                { 'Content-Type': file.type },
                                { success_action_status: '200' },
                                { 'x-amz-algorithm': 'AWS4-HMAC-SHA256' },
                                { key: file.meta.key },
                                { 'x-amz-credential': credentials },
                                { 'x-amz-date': date },
                                { 'x-amz-meta-qqfilename': file.name },
                                ['content-length-range', 0, 104857600],
                            ],
                        }),
                    });

                    if (response.status != 200) {
                        const err = await response.text();
                        throw new Error('upload signing error: ' + err);
                    }

                    const { policy, signature } = await response.json();
                    return {
                        method: 'POST',
                        url: requestEndpoint,
                        fields: {
                            key: file.meta.key,
                            'Content-Type': file.type,
                            success_action_status: 200,
                            acl: 'private',
                            'x-amz-meta-qqfilename': file.name,
                            'x-amz-algorithm': 'AWS4-HMAC-SHA256',
                            'x-amz-credential': credentials,
                            'x-amz-date': date,
                            policy: policy,
                            'x-amz-signature': signature,
                        },
                    };
                } else {
                    const response = await fetch(signatureEndpoint, {
                        method: 'POST',
                        headers: {
                            accept: 'application/json',
                            'X-CSRF-Token': csrfToken(),
                        },
                        body: JSON.stringify({
                            expiration: new Date(Date.now() + 5 * Minute).toISOString(),
                            conditions: [
                                { acl: 'private' },
                                { bucket: bucket },
                                { 'Content-Type': file.type },
                                { 'Content-Disposition': 'attachment' },
                                { success_action_status: '200' },
                                { key: file.meta.key },
                                { 'x-amz-meta-qqfilename': file.name },
                                ['content-length-range', 0, 104857600],
                            ],
                        }),
                    });

                    if (response.status != 200) {
                        const err = await response.text();
                        throw new Error('upload signing error: ' + err);
                    }

                    const { policy, signature } = await response.json();
                    return {
                        method: 'POST',
                        url: requestEndpoint,
                        fields: {
                            key: file.meta.key,
                            'Content-Type': file.type,
                            'Content-Disposition': 'attachment',
                            success_action_status: 200,
                            acl: 'private',
                            'x-amz-meta-qqfilename': file.name,
                            AWSAccessKeyId: requestAccessKey,
                            policy: policy,
                            signature: signature,
                        },
                    };
                }
            },
            timeout: 0,
        });

    return uppy;
}
